import { useEffect, useRef } from 'react';

import { ActionMeta, GroupBase, InputActionMeta, OnChangeValue } from 'react-select-5';

import { Controls, Props } from './types';

export function useEditing<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(controls: Controls<Option, IsMulti, Group>, props: Props<Option, IsMulti, Group>) {
  const { inputValue, selectRef } = controls;
  const { editable, isMulti, value } = props;

  const localValueRef = useRef<Option | null>(null);

  const shouldExecute = editable && !isMulti;

  const onBlur = () => {
    if (!shouldExecute) return;

    if (!localValueRef.current) inputValue.setValue('');
  };

  const onChange = (option: OnChangeValue<Option, IsMulti>, meta: ActionMeta<Option>) => {
    if (!shouldExecute) return;

    if (!option) {
      localValueRef.current = null;
      inputValue.setValue('');
      return;
    }

    localValueRef.current = option as NonNullable<OnChangeValue<Option, false>>;
    inputValue.setValue(selectRef.current?.getOptionLabel(option as NonNullable<OnChangeValue<Option, false>>) || '');
  };

  const onInputChange = (value: string, meta: InputActionMeta) => {
    if (!shouldExecute || !localValueRef.current) return;

    if (meta.action === 'menu-close') {
      const optionLabel = selectRef.current?.getOptionLabel(localValueRef.current);

      if (!optionLabel) return;

      inputValue.setValue(optionLabel);
    }
  };

  useEffect(() => {
    if (!shouldExecute || !value) return;

    localValueRef.current = value as NonNullable<Option>;

    const optionLabel = selectRef.current?.getOptionLabel(value as NonNullable<Option>);

    if (!optionLabel) return;

    inputValue.setValue(optionLabel);
  }, [JSON.stringify(value || '')]);

  return {
    onBlur,
    onChange,
    onInputChange,
  };
}
